import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { formatDistanceToNowStrict } from "date-fns";
import { Copy, MoreHorizontal, Trash2 } from "lucide-react";
import { mutate } from "swr";
import { Badge } from "~/components/ui/badge";
import { Button } from "~/components/ui/button";
import { Card } from "~/components/ui/card";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "~/components/ui/dropdown-menu";
import useToast from "~/components/ui/toast";
import { type ViewMode } from "~/hooks/useViewMode";
import { cn } from "~/lib/utils";
import { useDuplicateAgent } from "~/pages/manifold-nextgen/hooks/useDuplicateAgent";
import { ROUTE_NEXTGEN_VIEW } from "~/router/constants";
import { Agent, AgentScope, agentListPath, deleteAgent } from "~/services/agents";
import { AgentAvatar } from "./AgentAvatar";
import { DeleteAgentDialog } from "./DeleteAgentDialog";
import { StatusBadge } from "./StatusBadge";

interface AgentCardProps {
  agent: Agent;
  canManage: boolean;
  view?: ViewMode;
}

interface AgentActionsProps {
  agent: Agent;
  canManage: boolean;
  handleDuplicate: () => void;
  setDeleteOpen: (open: boolean) => void;
}

const AgentActions = ({ agent, canManage, handleDuplicate, setDeleteOpen }: AgentActionsProps) => {
  if (!canManage) {
    return null;
  }
  return (
    <div onClick={(e) => e.stopPropagation()}>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            variant="ghost"
            size="icon"
            className={cn(
              "size-7 shrink-0 text-muted-foreground hover:bg-black/10 hover:text-foreground dark:hover:bg-white/10",
              "opacity-0 group-hover:opacity-100 focus-visible:opacity-100 data-[state=open]:opacity-100",
              "focus-visible:ring-0 focus-visible:border-transparent"
            )}
            aria-label={`Actions for ${agent.display_name}`}
          >
            <MoreHorizontal />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          <DropdownMenuItem onClick={handleDuplicate}>
            <Copy />
            Duplicate
          </DropdownMenuItem>
          {agent.scope !== AgentScope.SYSTEM && (
            <>
              <DropdownMenuSeparator />
              <DropdownMenuItem variant="destructive" onClick={() => setDeleteOpen(true)}>
                <Trash2 />
                Delete
              </DropdownMenuItem>
            </>
          )}
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
};

export const AgentCard = ({ agent, canManage, view = "card" }: AgentCardProps) => {
  const navigate = useNavigate();
  const { showSuccess, showError } = useToast();
  const duplicateAgentAction = useDuplicateAgent();
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  const handleDuplicate = () => duplicateAgentAction(agent.id, agent.display_name);

  const handleDelete = async () => {
    setIsDeleting(true);
    try {
      await deleteAgent(agent.id);
      await mutate(agentListPath());
      showSuccess(`"${agent.display_name}" deleted`);
    } catch {
      showError("Failed to delete agent");
    } finally {
      setIsDeleting(false);
      setDeleteOpen(false);
    }
  };

  const footer = (
    <div className="flex items-center justify-between gap-2">
      <div className="flex flex-wrap items-center gap-1.5">
        <StatusBadge status={agent.status} />
        {agent.scope === AgentScope.SYSTEM && (
          <Badge variant="outline" className="rounded-full">
            System
          </Badge>
        )}
      </div>
      <p className="shrink-0 text-xs text-muted-foreground">
        Updated {formatDistanceToNowStrict(new Date(agent.updated_at), { addSuffix: true })}
      </p>
    </div>
  );

  if (view === "list") {
    return (
      <>
        <div className="group relative">
          <div
            className={cn(
              "flex items-center gap-4 rounded-lg bg-card px-4 py-5",
              "cursor-pointer transition-colors duration-150 group-hover:bg-muted"
            )}
            onClick={() => navigate(`${ROUTE_NEXTGEN_VIEW}/agents/${agent.id}`)}
          >
            <AgentAvatar id={agent.id} name={agent.name} size={36} />
            <div className="min-w-0 flex-1">
              <span className="block truncate text-sm font-semibold text-foreground">
                {agent.display_name}
              </span>
              <p className="truncate text-xs text-muted-foreground">{agent.description}</p>
            </div>
            <div className="flex w-40 shrink-0 items-center gap-1.5">
              <StatusBadge status={agent.status} />
              {agent.scope === AgentScope.SYSTEM && (
                <Badge variant="outline" className="rounded-full">
                  System
                </Badge>
              )}
            </div>
            <p className="w-36 shrink-0 text-right text-xs text-muted-foreground">
              Updated {formatDistanceToNowStrict(new Date(agent.updated_at), { addSuffix: true })}
            </p>
            <AgentActions
              agent={agent}
              canManage={canManage}
              handleDuplicate={handleDuplicate}
              setDeleteOpen={setDeleteOpen}
            />
          </div>
        </div>
        <DeleteAgentDialog
          agent={agent}
          open={deleteOpen}
          onOpenChange={setDeleteOpen}
          onConfirm={handleDelete}
          isDeleting={isDeleting}
        />
      </>
    );
  }

  return (
    <>
      <div className="group relative h-full transition-transform duration-200 ease-[cubic-bezier(0.165,0.84,0.44,1)] hover:scale-[1.02]">
        <Card
          className={cn(
            "h-full gap-0 overflow-hidden p-0",
            "cursor-pointer shadow-[0_1px_2px_rgba(15,23,42,0.03)]"
          )}
          onClick={() => navigate(`${ROUTE_NEXTGEN_VIEW}/agents/${agent.id}`)}
        >
          <div className="flex items-center gap-3 bg-[var(--card-header-bg)] px-5 py-4">
            <AgentAvatar id={agent.id} name={agent.name} size={44} />
            <div className="min-w-0 flex-1">
              <span className="block text-base font-semibold leading-tight text-foreground">
                {agent.display_name}
              </span>
            </div>
            <AgentActions
              agent={agent}
              canManage={canManage}
              handleDuplicate={handleDuplicate}
              setDeleteOpen={setDeleteOpen}
            />
          </div>

          <div className="flex flex-1 flex-col gap-3 px-5 pb-3 pt-3">
            <p className="line-clamp-3 flex-1 text-sm text-muted-foreground">{agent.description}</p>
            {footer}
          </div>
        </Card>

        <div
          aria-hidden="true"
          className={cn(
            "pointer-events-none absolute inset-0 rounded-xl",
            "opacity-0 shadow-[0_4px_10px_rgba(15,23,42,0.06)]",
            "transition-opacity duration-200 ease-[cubic-bezier(0.165,0.84,0.44,1)] group-hover:opacity-100"
          )}
        />
      </div>

      <DeleteAgentDialog
        agent={agent}
        open={deleteOpen}
        onOpenChange={setDeleteOpen}
        onConfirm={handleDelete}
        isDeleting={isDeleting}
      />
    </>
  );
};
